import React, { useState, useMemo } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Form,
  Modal,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import {
  FaUserPlus,
  FaEdit,
  FaTrash,
  FaShieldAlt,
  FaUserShield,
  FaCheckCircle,
  FaTimesCircle,
  FaLock,
} from "react-icons/fa";
import {
  useGetAllUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} from "../../../redux/api/UserApi/userApi";
import { sidebarItems } from "../Navbars/SideBarData";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import DeleteModel from "../../../Components/DeleteModel";
import { message } from "antd";

const Users = () => {
  const { data: users, isLoading } = useGetAllUsersQuery();
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "admin",
    permissions: [],
  });

  // Get all unique modules from sidebar data for permissions
  const availableModules = useMemo(() => {
    return sidebarItems
      .filter((item) => item.module)
      .map((item) => ({ label: item.label, value: item.module }));
  }, []);

  const handleOpenModal = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: "", // Don't show password on edit
        role: user.role,
        permissions: user.permissions || [],
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "admin",
        permissions: [],
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (moduleValue) => {
    setFormData((prev) => {
      const isChecked = prev.permissions.includes(moduleValue);
      if (isChecked) {
        return {
          ...prev,
          permissions: prev.permissions.filter((p) => p !== moduleValue),
        };
      } else {
        return {
          ...prev,
          permissions: [...prev.permissions, moduleValue],
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await updateUser({ id: selectedUser.id, ...formData }).unwrap();
        message.success("User updated successfully");
      } else {
        await createUser(formData).unwrap();
        message.success("User created successfully");
      }
      handleCloseModal();
    } catch (error) {
      message.error(error.data?.message || "Operation failed");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteUser(selectedUser.id).unwrap();
      message.success("User deleted successfully");
      setShowDeleteModal(false);
    } catch (error) {
      message.error(error.data?.message || "Delete failed");
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 fade-in">
      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaUserShield className="text-primary me-2" />
            User Access Control
          </h2>
          <p className="text-muted mb-0">
            {" "}
            Manage administrator accounts and module permissions
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill fw-semibold"
          style={{ backgroundColor: "#0C95CC", border: "none" }}
        >
          <FaUserPlus /> Add New User
        </Button>
      </div>

      {/* Stats Summary */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-primary-soft text-primary me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaShieldAlt size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{users?.length || 0}</h4>
                <div className="text-muted small fw-semibold">
                  Total Administrators
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-success-soft text-success me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaUserShield size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">
                  {users?.filter((u) => u.role === "superadmin").length || 0}
                </h4>
                <div className="text-muted small fw-semibold">Super Admins</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-warning-soft text-warning me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaLock size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">Active</h4>
                <div className="text-muted small fw-semibold">RBAC Status</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Users Table */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4 border-0">User Identifier</th>
                <th className="border-0">Account Role</th>
                <th className="border-0">Module Permissions</th>
                <th className="text-center border-0">Status</th>
                <th className="text-end pe-4 border-0">Control</th>
              </tr>
            </thead>
            <tbody>
              {users?.map((user) => (
                <tr key={user.id}>
                  <td className="ps-4 py-3">
                    <div className="d-flex align-items-center">
                      <div
                        className="avatar bg-primary-soft text-primary me-3 rounded-circle d-flex align-items-center justify-content-center fw-bold"
                        style={{ width: "40px", height: "40px" }}
                      >
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-bold text-dark">{user.username}</div>
                        <div className="text-muted small">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge
                      bg={
                        user.role === "superadmin"
                          ? "danger"
                          : user.role === "admin"
                            ? "primary"
                            : "info"
                      }
                      className="text-uppercase p-2 rounded-3"
                    >
                      {user.role}
                    </Badge>
                  </td>
                  <td>
                    <div
                      className="d-flex flex-wrap gap-1"
                      style={{ maxWidth: "300px" }}
                    >
                      {user.role === "superadmin" ? (
                        <Badge
                          bg="warning"
                          className="text-dark py-1 px-2 border"
                        >
                          FULL ACCESS
                        </Badge>
                      ) : user.permissions?.length > 0 ? (
                        user.permissions.map((p) => (
                          <Badge
                            key={p}
                            bg="light"
                            className="text-primary border fw-normal py-1 px-2"
                          >
                            {p}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted small italic">
                          No modules granted
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-center">
                    {user.status ? (
                      <span className="text-success small fw-bold d-flex align-items-center justify-content-center gap-1">
                        <FaCheckCircle size={12} /> Active
                      </span>
                    ) : (
                      <span className="text-danger small fw-bold d-flex align-items-center justify-content-center gap-1">
                        <FaTimesCircle size={12} /> Blocked
                      </span>
                    )}
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="white"
                        size="sm"
                        onClick={() => handleOpenModal(user)}
                        className="btn-action shadow-sm border"
                      >
                        <FaEdit className="text-primary" />
                      </Button>
                      {user.role !== "superadmin" && (
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteModal(true);
                          }}
                          className="btn-action shadow-sm border"
                        >
                          <FaTrash className="text-danger" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 px-4 pt-4">
          <Modal.Title className="fw-bold">
            <FaUserShield className="text-primary me-2" />
            {selectedUser
              ? "Modify Access Privileges"
              : "Onboard New Administrator"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="px-4 pb-4">
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label className="small fw-bold text-muted text-uppercase">
                  Username
                </Form.Label>
                <Form.Control
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                  className="rounded-3 py-2 shadow-none border"
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label className="small fw-bold text-muted text-uppercase">
                  Email Address
                </Form.Label>
                <Form.Control
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="name@ideal.com"
                  required
                  className="rounded-3 py-2 shadow-none border"
                />
              </Col>
            </Row>

            {!selectedUser && (
              <Row>
                <Col md={12} className="mb-3">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    Generated Password
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      required
                      className="rounded-3 py-2 shadow-none border"
                    />
                  </InputGroup>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={12} className="mb-4">
                <Form.Label className="small fw-bold text-muted text-uppercase">
                  Account Privilege Role
                </Form.Label>
                <Form.Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="rounded-3 py-2 shadow-none border"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                  <option value="superadmin">Superadmin</option>
                </Form.Select>
              </Col>
            </Row>

            <div className="bg-light p-4 rounded-4 border">
              <div className="d-flex align-items-center mb-3">
                <FaLock className="text-primary me-3" size={18} />
                <h6 className="fw-bold mb-0">Accessible Dashboard Modules</h6>
              </div>
              <p className="small text-muted mb-4 px-1">
                Select specific areas of the dashboard this user is allowed to
                manage. Superadmins automatically have master access.
              </p>

              <Row className="g-3">
                {availableModules.map((module) => (
                  <Col xs={6} md={4} key={module.value}>
                    <div
                      className={`p-3 border rounded-3 cursor-pointer transition-all ${
                        formData.permissions.includes(module.value)
                          ? "bg-primary-soft border-primary"
                          : "bg-white"
                      }`}
                      onClick={() => handlePermissionChange(module.value)}
                      style={{ cursor: "pointer" }}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`module-${module.value}`}
                        label={module.label}
                        checked={formData.permissions.includes(module.value)}
                        onChange={() => {}} // Controlled by div click
                        className="mb-0 fw-semibold extra-small"
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 px-4 pb-4 pt-0">
            <Button
              variant="light"
              onClick={handleCloseModal}
              className="px-4 py-2 rounded-pill fw-semibold"
            >
              Discard Changes
            </Button>
            <Button
              type="submit"
              className="px-5 py-2 rounded-pill fw-semibold shadow-sm"
              style={{ backgroundColor: "#0C95CC", border: "none" }}
              disabled={isCreating || isUpdating}
            >
              {isCreating || isUpdating ? (
                <Spinner animation="border" size="sm" />
              ) : selectedUser ? (
                "Update Access"
              ) : (
                "Grant Access"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={() => setShowDeleteModal(false)}
        YES={handleDeleteConfirm}
        DESCRIPTION={`Are you sure you want to revoke access for "${selectedUser?.username}"`}
        DELETETITLE="Revoke Access"
      />
    </div>
  );
};

export default Users;
